export const CSRF_HEADER_NAME = 'X-CSRF-Token';

const PROD_CSRF_COOKIE_NAME = '__Secure-csrf-token';
const DEV_CSRF_COOKIE_NAME = 'csrf-token';

const tokenCacheByApiDomain = new Map<string, string>();
const inFlightByApiDomain = new Map<string, Promise<string>>();

function readCookieValue(cookieName: string): string | null {
    const raw = document.cookie;
    if (!raw) return null;

    const parts = raw.split(';');

    for (const part of parts) {
        const trimmed = part.trim();
        if (!trimmed) continue;

        const eq = trimmed.indexOf('=');
        const name = eq >= 0 ? trimmed.slice(0, eq) : trimmed;
        if (name !== cookieName) continue;

        const value = eq >= 0 ? trimmed.slice(eq + 1) : '';
        return value ? decodeURIComponent(value) : '';
    }

    return null;
}

function readExistingCsrfTokenFromCookies(): string | null {
    return readCookieValue(PROD_CSRF_COOKIE_NAME) ?? readCookieValue(DEV_CSRF_COOKIE_NAME);
}

export async function ensureCsrfToken(apiDomain: string, params?: { forceRefresh?: boolean }): Promise<string> {
    const forceRefresh = Boolean(params?.forceRefresh);

    if (!forceRefresh) {
        const cached = tokenCacheByApiDomain.get(apiDomain);
        if (cached) return cached;

        const existing = readExistingCsrfTokenFromCookies();

        if (existing) {
            tokenCacheByApiDomain.set(apiDomain, existing);
            return existing;
        }
    }

    const inFlight = inFlightByApiDomain.get(apiDomain);
    if (inFlight) return inFlight;

    const promise = (async (): Promise<string> => {
        const response = await fetch(`${apiDomain}/api/csrf`, {
            method: 'GET',
            credentials: 'include',
            cache: 'no-store'
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch CSRF token (HTTP ${response.status})`);
        }

        const json = (await response.json()) as { success?: boolean; csrfToken?: string };
        const tokenFromBody = typeof json.csrfToken === 'string' ? json.csrfToken : null;
        const tokenFromCookie = readExistingCsrfTokenFromCookies();
        const token = tokenFromCookie ?? tokenFromBody;

        if (!token) throw new Error('CSRF token missing from response');
        tokenCacheByApiDomain.set(apiDomain, token);
        return token;
    })();

    inFlightByApiDomain.set(apiDomain, promise);

    try {
        return await promise;
    } finally {
        inFlightByApiDomain.delete(apiDomain);
    }
}

export async function buildHeadersWithCsrfToken(params: {
    apiDomain: string;
    headers?: HeadersInit | undefined;
    forceRefresh?: boolean;
}): Promise<Headers> {
    const csrfToken = await ensureCsrfToken(params.apiDomain, { forceRefresh: params.forceRefresh });
    const headers = new Headers(params.headers);
    headers.set(CSRF_HEADER_NAME, csrfToken);
    return headers;
}
